import { Grid, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import _ from "lodash";
import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { canAccessDevelopmentOnlyFeatures } from "utils/EnvironmentUtils";
import { useStore } from "utils/useStore";
import { ObjectFilterConflict } from "../DashboardStore";

type Props = {
    isOpen: boolean;
    conflicts: ObjectFilterConflict[];
    onCancel: () => void;
    onContinue: () => void | Promise<void>;
    isContinueLoading?: boolean;
};

function formatConflictValue(
    value: unknown,
    resolveName?: (id: string) => string,
): string {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") {
        return resolveName ? resolveName(value) : value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return `${value}`;
    }

    return JSON.stringify(value);
}

const ObjectFilterConflictDialog: React.FC<Props> = observer(({
    isOpen,
    conflicts,
    onCancel,
    onContinue,
    isContinueLoading,
}) => {
    const applicationFilterItemsStore = useStore(ApplicationFilterItemsStore);
    const applicationFilterValuesStore = useStore(ApplicationFilterValuesStore);
    const canViewDevelopmentDetails = canAccessDevelopmentOnlyFeatures({
        requireUltraUser: true,
    });
    const hasConflicts = conflicts.length > 0;
    const showDevelopmentDetails = canViewDevelopmentDetails && hasConflicts;
    // Conflict labels are plain strings, so this map qualifies the two identically-titled Theme
    // filters by source. See ApplicationFilterItemsStore.plainTextFilterTitleByKey.
    const filterTitleByKey = applicationFilterItemsStore.plainTextFilterTitleByKey;

    // Agents and agent managers are the only conflicts whose values are opaque ids -- every other
    // filter already conflicts on something readable. Unresolvable ids fall back to the id itself,
    // so a stale selection still shows which value conflicted.
    const nameResolverByFilterKey = useMemo<
        Record<string, ((id: string) => string) | undefined>
    >(() => {
        const resolveFrom =
            (nameById: Map<string, string>) => (id: string) =>
                nameById.get(id) || id;

        return {
            agentIds: resolveFrom(
                new Map(
                    (applicationFilterValuesStore.agents ?? []).map((agent) => [
                        `${agent.id ?? ""}`,
                        agent.fullName?.trim() || agent.agentEmail || "",
                    ]),
                ),
            ),
            agentManagerIds: resolveFrom(
                new Map(
                    (applicationFilterValuesStore.agentManagers ?? []).map(
                        (manager) => [
                            `${manager.id ?? ""}`,
                            manager.fullName?.trim() || "",
                        ],
                    ),
                ),
            ),
        };
    }, [
        applicationFilterValuesStore.agents,
        applicationFilterValuesStore.agentManagers,
    ]);

    const groupedConflicts = useMemo<Record<string, ObjectFilterConflict[]>>(
        () => {
            if (!showDevelopmentDetails) return {};

            return _.groupBy(
                conflicts,
                (conflict) =>
                    `${conflict.componentId}::${conflict.componentTitle}`,
            );
        },
        [conflicts, showDevelopmentDetails],
    );

    return (
        <AcxDialog
            id="object-filter-conflict-dialog"
            isOpen={isOpen}
            onClose={onCancel}
            title="Some chart filters will be adjusted"
            text="Applying these dashboard filters will adjust some chart filters to keep them aligned. Would you like to proceed?"
            maxWidth="sm"
            contentWidth="420px"
            hideCloseButton
            textAlignCenterTitle
            textAlignCenterContent
            centerActionButtons
            dialogContentChildren={
                showDevelopmentDetails ? (
                    <Grid
                        container
                        direction="column"
                        rowGap={1.5}
                        sx={{ mt: 1.5 }}
                    >
                        <Grid item>
                            <Typography variant="subtitle2">
                                Development-only conflict details
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography
                                variant="body2"
                                sx={{ color: "text.secondary" }}
                            >
                                Conflicts occur when object-level filters no
                                longer fit within the dashboard filter
                                selection. Confirming will clamp some filters
                                and remove others based on each filter's
                                conflict rule.
                            </Typography>
                        </Grid>

                        {Object.entries(groupedConflicts).map(
                            ([groupKey, entries]) => {
                                const componentTitle =
                                    groupKey.split("::")[1] ?? "Card";

                                return (
                                    <Grid
                                        item
                                        key={groupKey}
                                        sx={(theme) => ({
                                            border: `1px solid ${theme.palette.gray[200]}`,
                                            borderRadius: 1,
                                            p: 1.5,
                                        })}
                                    >
                                        <Typography variant="subtitle2">
                                            {componentTitle}
                                        </Typography>
                                        <Grid
                                            container
                                            direction="column"
                                            rowGap={0.5}
                                            sx={{ mt: 0.75 }}
                                        >
                                            {entries.map((conflict, index) => (
                                                <Typography
                                                    key={`${conflict.filterKey}-${index}`}
                                                    variant="body2"
                                                    sx={{
                                                        color: "text.secondary",
                                                    }}
                                                >
                                                    {filterTitleByKey.get(
                                                        conflict.filterKey,
                                                    ) ??
                                                        _.startCase(
                                                            conflict.filterKey,
                                                        )}
                                                    :{" "}
                                                    {conflict.conflictingValues
                                                        .map((value) =>
                                                            formatConflictValue(
                                                                value,
                                                                nameResolverByFilterKey[
                                                                    conflict
                                                                        .filterKey
                                                                ],
                                                            ),
                                                        )
                                                        .join(", ")}
                                                </Typography>
                                            ))}
                                        </Grid>
                                    </Grid>
                                );
                            },
                        )}
                    </Grid>
                ) : undefined
            }
        >
            <AcxButton
                id="object-filter-conflict-continue"
                color="primary"
                onClick={onContinue}
                loading={isContinueLoading}
            >
                Confirm
            </AcxButton>
        </AcxDialog>
    );
});

export default ObjectFilterConflictDialog;
